import { ComponentType, ReactElement } from "react";
import {
  FieldValues,
  ArrayPath,
  UseFormReturn,
  UseFormRegisterReturn,
  ControllerProps,
} from "react-hook-form";
import { AnySchema } from "yup";
import { RenderInputProps } from "components/Shared/Forms/FormField";

export type GetResultContext = {
  filterDefinition: FilterDefinition;
  filterVariantDefinition: FilterVariantDefinition;
};

export type GetResultFunction<V = unknown, R = unknown> = (
  value: V,
  context: GetResultContext,
) => R;

type BaseFilterVariantDefinition = {
  getResult?: GetResultFunction;
  id: string;
  label: string;
  schema?: AnySchema;
};

type BaseRegisterInputDefinition = {
  InputComponent?: ComponentType<RenderInputProps>;
  renderInput?: (props: UseFormRegisterReturn) => ReactElement;
};

type BaseControlledInputDefinition = {
  renderControlledInput: ControllerProps["render"];
};

type RegisterInputFilterVariantDefinition = BaseFilterVariantDefinition &
  BaseRegisterInputDefinition;

type ControlledInputFilterVariantDefinition = BaseFilterVariantDefinition &
  BaseControlledInputDefinition;

export type FilterVariantDefinition =
  | RegisterInputFilterVariantDefinition
  | ControlledInputFilterVariantDefinition;

export function getIsControlledInputFilterVariantDefinition(
  filterVariantDefinition: FilterVariantDefinition,
): filterVariantDefinition is ControlledInputFilterVariantDefinition {
  return "renderControlledInput" in filterVariantDefinition;
}

type BaseFilterDefinition = {
  id: string;
  label: string;
  groupLabel?: string | string[];
  variants: FilterVariantDefinition[];
  defaultVariant?: string | null;
  equalityVariant?: string;
  getResult?: GetResultFunction;
  schema?: AnySchema;
  isHidden?: boolean;
};

export type RegisterInputFilterDefinition = BaseFilterDefinition &
  BaseRegisterInputDefinition;

export type ControlledInputFilterDefinition = BaseFilterDefinition &
  BaseControlledInputDefinition;

export type FilterDefinition =
  | RegisterInputFilterDefinition
  | ControlledInputFilterDefinition;

export function getIsControlledInputFilterDefinition(
  filterDefinition: FilterDefinition,
): filterDefinition is ControlledInputFilterDefinition {
  return "renderControlledInput" in filterDefinition;
}

export type AppliedFilter = {
  filter: string;
  variant: string;
  value?: unknown;
};

export type BaseFilterFormProps<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
> = {
  filterDefinitions: FilterDefinition[];
  filtersFormName: TName;
  formContext: UseFormReturn<TFieldValues>;
};

export type FilterVariantDefinitionOverrides<
  TFVD extends FilterVariantDefinition = FilterVariantDefinition,
> = Partial<Record<TFVD["id"], Partial<Omit<TFVD, "id">>>>;

export function getFilterVariantDefinitionsWithOverrides<
  TFVD extends FilterVariantDefinition,
>(
  filterVariantDefinitions: TFVD[],
  overrides?: FilterVariantDefinitionOverrides<TFVD>,
) {
  if (!overrides) return filterVariantDefinitions;
  return filterVariantDefinitions.map((fvd) => {
    const fvdOverride = overrides[fvd.id as TFVD["id"]];
    if (!fvdOverride) return fvd;
    return { ...fvd, ...fvdOverride };
  });
}

export function combineGroupLabels(
  newLabels: string | string[] | undefined,
  existingLabels: string | string[] | undefined,
) {
  if (!newLabels && !existingLabels) return;
  if (!newLabels) return existingLabels;
  if (!existingLabels) return newLabels;

  const combined = Array.isArray(newLabels) ? newLabels : [newLabels];

  if (Array.isArray(existingLabels)) {
    combined.push(...existingLabels);
  } else {
    combined.push(existingLabels);
  }
  return combined;
}
